import type { JournalEntry, GroupedInsights } from '../../types/journal';
import { calculateInsights } from '../../utils/insights';

interface InsightsPanelProps {
  entries: JournalEntry[];
}

function InsightsGroup({ group, showHeader }: { group: GroupedInsights; showHeader: boolean }) {
  const { insights, entryCount } = group;

  const hasTimingData =
    insights.averageOnsetMinutes != null ||
    insights.averagePeakMinutes != null ||
    insights.averageDurationMinutes != null;

  return (
    <div className="insights-group">
      {showHeader && (
        <h4 className="insights-group-header">{group.cannabinoidType}</h4>
      )}

      <dl className="insights-metrics">
        <div className="insights-metric">
          <dt>Average Dose</dt>
          <dd>{insights.averageDoseMg} mg</dd>
        </div>

        {hasTimingData ? (
          <div className="insights-timing">
            <dt className="insights-timing-label">Timing Averages</dt>
            <dd className="insights-timing-values">
              {insights.averageOnsetMinutes != null && (
                <span className="insights-timing-item">
                  Onset: {insights.averageOnsetMinutes} min
                </span>
              )}
              {insights.averagePeakMinutes != null && (
                <span className="insights-timing-item">
                  Peak: {insights.averagePeakMinutes} min
                </span>
              )}
              {insights.averageDurationMinutes != null && (
                <span className="insights-timing-item">
                  Duration: {insights.averageDurationMinutes} min
                </span>
              )}
            </dd>
          </div>
        ) : (
          <div className="insights-metric insights-metric-empty">
            <dt>Timing Averages</dt>
            <dd>Log timing data on more entries to see averages</dd>
          </div>
        )}

        {insights.topEffects.length > 0 && (
          <div className="insights-metric">
            <dt>Top Effects</dt>
            <dd className="insights-effects-list">
              {insights.topEffects.map(({ effect, count }) => (
                <span key={effect} className="insights-effect-tag">
                  {effect} ({count})
                </span>
              ))}
            </dd>
          </div>
        )}

        {insights.consumptionFrequencyPerWeek != null && (
          <div className="insights-metric">
            <dt>Consumption Frequency</dt>
            <dd>{insights.consumptionFrequencyPerWeek} entries/week</dd>
          </div>
        )}
      </dl>

      <p className="insights-entry-count">Based on {entryCount} entries</p>
    </div>
  );
}

export function InsightsPanel({ entries }: InsightsPanelProps) {
  if (entries.length < 3) {
    return (
      <section className="insights-panel" aria-label="Insights">
        <h3 className="insights-heading">Insights</h3>
        <p className="insights-empty-message">
          Log at least 3 entries to see insights
        </p>
      </section>
    );
  }

  const groupedInsights = calculateInsights(entries);

  if (groupedInsights.length === 0) {
    return (
      <section className="insights-panel" aria-label="Insights">
        <h3 className="insights-heading">Insights</h3>
        <p className="insights-empty-message">
          Log at least 3 entries to see insights
        </p>
      </section>
    );
  }

  const showGroupHeaders = groupedInsights.length > 1;

  return (
    <section className="insights-panel" aria-label="Insights">
      <h3 className="insights-heading">Insights</h3>
      {groupedInsights.map((group) => (
        <InsightsGroup
          key={group.cannabinoidType}
          group={group}
          showHeader={showGroupHeaders}
        />
      ))}
    </section>
  );
}
